import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, MessageCircle, Mic, Image, Globe, Crown, User, LogOut, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const Guide = () => {
  const sections = [
    {
      icon: <User size={32} />,
      title: "1. Créer un compte",
      steps: [
        "Cliquez sur 'S'inscrire' depuis la page d'accueil",
        "Remplissez votre nom, email et mot de passe (minimum 6 caractères)",
        "Validez pour créer votre compte",
        "Vous serez automatiquement connecté"
      ]
    },
    {
      icon: <Crown size={32} />,
      title: "2. S'abonner",
      steps: [
        "Après inscription, vous êtes redirigé vers la page d'abonnement",
        "Consultez les fonctionnalités Premium incluses",
        "Cliquez sur 'S'abonner maintenant'",
        "Complétez le paiement via Stripe dans la nouvelle fenêtre",
        "Revenez sur l'application et profitez de toutes les fonctionnalités"
      ]
    },
    {
      icon: <MessageCircle size={32} />,
      title: "3. Discuter par texte",
      steps: [
        "Accédez au chat depuis la page principale",
        "Tapez votre message dans la zone de saisie en bas",
        "Appuyez sur Entrée ou cliquez sur l'icône d'envoi",
        "Coach Life vous répond avec empathie et intelligence",
        "Continuez la conversation naturellement"
      ]
    },
    {
      icon: <Mic size={32} />,
      title: "4. Mode vocal en temps réel",
      steps: [
        "Cliquez sur le bouton vocal flottant en bas à droite (photo de Coach Life)",
        "Autorisez l'accès au microphone si demandé",
        "Attendez que le bouton devienne vert (connecté)",
        "Parlez naturellement - Coach Life vous écoute",
        "Recevez des réponses vocales en temps réel",
        "Cliquez à nouveau pour désactiver le mode vocal"
      ]
    },
    {
      icon: <Image size={32} />,
      title: "5. Envoyer des fichiers",
      steps: [
        "Cliquez sur l'icône trombone (📎) dans la zone de saisie",
        "Sélectionnez une image, vidéo ou document (max 10 MB)",
        "Aperçu de votre fichier s'affiche",
        "Ajoutez un message optionnel",
        "Cliquez sur Envoyer",
        "Coach Life analyse votre fichier et répond avec insights"
      ],
      note: "Formats supportés: Images (JPG, PNG, GIF), Vidéos (MP4, WebM), Documents (PDF, DOC, TXT)"
    },
    {
      icon: <Globe size={32} />,
      title: "6. Changer de langue",
      steps: [
        "Cliquez sur le sélecteur de langue en haut à droite (icône globe)",
        "Choisissez parmi 16 langues disponibles",
        "Coach Life s'adapte et répond dans la langue choisie",
        "Langues: Français, Anglais, Espagnol, Italien, Allemand, Portugais, Chinois, Japonais, Arabe, Coréen, Turc, Russe, Persan, Hindi, Tamazight, Ukrainien"
      ]
    },
    {
      icon: <BookOpen size={32} />,
      title: "7. Gérer l'historique",
      steps: [
        "Bouton 'Clear' en haut à droite: Efface l'historique de la session actuelle",
        "Bouton 'New': Crée une nouvelle session de conversation",
        "Toutes vos conversations sont sauvegardes automatiquement",
        "Accédez à l'historique complet à tout moment"
      ]
    },
    {
      icon: <LogOut size={32} />,
      title: "8. Se déconnecter",
      steps: [
        "Sur mobile: Ouvrez le menu hamburger (☰) en haut à droite",
        "Trouvez l'option de déconnexion dans votre profil",
        "Sur desktop: Accédez aux paramètres du compte",
        "Cliquez sur 'Se déconnecter'"
      ]
    }
  ];

  const tips = [
    {
      title: "💡 Conseil: Conversations naturelles",
      description: "Parlez à Coach Life comme à un ami. Pas besoin de commandes spéciales - soyez vous-même!"
    },
    {
      title: "🎯 Conseil: Contexte",
      description: "Coach Life se souvient des 10 derniers messages. Pour de meilleurs résultats, fournissez du contexte."
    },
    {
      title: "🖼️ Conseil: Images",
      description: "Envoyez des photos pour obtenir des analyses détaillées et des conseils personnalisés."
    },
    {
      title: "🎤 Conseil: Mode vocal",
      description: "Le mode vocal est idéal pour des conversations fluides et naturelles, comme un vrai coach."
    },
    {
      title: "🌍 Conseil: Multilangue",
      description: "Vous pouvez changer de langue à tout moment. Coach Life s'adapte instantanément."
    }
  ];

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: 'linear-gradient(140deg, #08101e 0%, #0f172a 55%, #0b3b4b 100%)'
      }}
    >
      {/* Header */}
      <motion.header
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{
          background: 'rgba(11, 18, 32, 0.7)',
          borderColor: 'rgba(var(--line), 0.6)'
        }}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />
            <Link
              to="/login"
              className="px-4 py-2 rounded-lg transition-all"
              style={{ background: 'var(--accent-cyan)', color: '#0b1220' }}
            >
              Retour
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <BookOpen size={64} className="mx-auto mb-4" style={{ color: 'var(--accent-cyan)' }} />
          <h1
            className="text-4xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--accent-cyan) 0%, var(--accent-ocean) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Guide d'utilisation
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Découvrez comment tirer le meilleur parti de Coach Life
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="backdrop-blur-xl rounded-2xl p-6 border"
              style={{
                background: 'rgba(var(--bg-elev-1), 0.8)',
                borderColor: 'rgba(var(--line), 0.6)'
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: 'var(--accent-cyan)', color: '#0b1220' }}
                >
                  {section.icon}
                </div>
                <div className="flex-1">
                  <h2
                    className="text-xl font-bold mb-4"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {section.title}
                  </h2>
                  <ul className="space-y-2">
                    {section.steps.map((step, stepIndex) => (
                      <li
                        key={stepIndex}
                        className="flex items-start gap-2"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        <ChevronRight
                          size={16}
                          className="flex-shrink-0 mt-1"
                          style={{ color: 'var(--accent-ocean)' }}
                        />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                  {section.note && (
                    <div
                      className="mt-3 p-3 rounded-lg"
                      style={{ background: 'rgba(6, 182, 212, 0.1)', borderLeft: '3px solid var(--accent-cyan)' }}
                    >
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <strong>Note:</strong> {section.note}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tips Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12"
        >
          <h2
            className="text-2xl font-bold mb-6 text-center"
            style={{ color: 'var(--text-primary)' }}
          >
            Conseils & Astuces
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.9 + index * 0.1 }}
                className="backdrop-blur-xl rounded-xl p-4 border"
                style={{
                  background: 'rgba(var(--bg-elev-2), 0.6)',
                  borderColor: 'rgba(var(--line), 0.4)'
                }}
              >
                <h3
                  className="font-semibold mb-2"
                  style={{ color: 'var(--accent-cyan)' }}
                >
                  {tip.title}
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {tip.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
          className="mt-12 text-center"
        >
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, var(--accent-cyan) 0%, var(--accent-ocean) 100%)',
              color: '#0b1220',
              boxShadow: '0 10px 30px rgba(6, 182, 212, 0.3)'
            }}
          >
            Commencer maintenant
            <ChevronRight size={20} />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Guide;
